/**
 * Single source of truth for cookie consent.
 *
 * Deliberately framework-free so that non-React modules (firebaseAnalytics.js)
 * can import it without pulling in React or a context.
 *
 * Stored in a first-party cookie rather than localStorage because:
 *   - it can be read synchronously on first paint (no banner flash)
 *   - it survives the react-snap prerender pass predictably
 *   - it is shared with any subdomain you add later (app.promptera.io)
 */

const COOKIE_NAME = 'promptera_consent';
const MAX_AGE_DAYS = 180; // ~6 months. Do not re-ask a refuser sooner than this.

/**
 * Bump this only when you add a NEW category or a new vendor to an existing
 * one. Bumping invalidates stored choices and re-asks everyone, which is
 * correct when the thing they agreed to has changed — and obnoxious otherwise.
 */
const POLICY_VERSION = 2;

const EVENT_NAME = 'promptera:consent-change';

/** Categories the user can decline. "necessary" is not listed: it is not optional. */
export const CATEGORIES = ['analytics'];

/* ------------------------------------------------------------------ */
/* cookie primitives                                                   */
/* ------------------------------------------------------------------ */

function readRawCookies(name) {
  if (typeof document === 'undefined') return []; // prerender / SSR
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('='))
    .filter(([cookieName]) => cookieName === name)
    .map(([, ...valueParts]) => decodeURIComponent(valueParts.join('=')));
}

function cookieDomainAttribute() {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname.toLowerCase();
  return host === 'promptera.io' || host.endsWith('.promptera.io')
    ? '; Domain=.promptera.io'
    : '';
}

function writeRawCookie(name, value, days) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  deleteRawCookie(name);
  document.cookie =
    `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax${secure}${cookieDomainAttribute()}`;
}

function deleteRawCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  const domain = cookieDomainAttribute();
  if (domain) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain}`;
  }
}

/* ------------------------------------------------------------------ */
/* public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * @returns {{analytics: boolean, ts: number, v: number} | null}
 *          null means "this person has not been asked yet" — show the banner.
 */
export function getConsent() {
  const values = readRawCookies(COOKIE_NAME);

  for (const raw of values) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed.v !== POLICY_VERSION) continue;
      if (typeof parsed.analytics !== 'boolean') continue;
      return parsed;
    } catch {
      // Ignore malformed or obsolete duplicate cookies and keep looking.
    }
  }

  return null;
}

/**
 * Record a decision. Pass an object keyed by category, e.g. { analytics: true }.
 * Missing categories default to false — silence is never consent.
 */
export function setConsent(choice) {
  const record = { v: POLICY_VERSION, ts: Date.now() };
  CATEGORIES.forEach((c) => {
    record[c] = choice[c] === true;
  });

  writeRawCookie(COOKIE_NAME, JSON.stringify(record), MAX_AGE_DAYS);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: record }));
  }
  return record;
}

/** The gate. Import this anywhere you are about to touch a third party. */
export function hasConsent(category) {
  const consent = getConsent();
  return consent ? consent[category] === true : false;
}

/** Wipes the record so the banner reappears. Used by "Reset choices". */
export function clearConsent() {
  deleteRawCookie(COOKIE_NAME);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
  }
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function onConsentChange(callback) {
  if (typeof window === 'undefined') return () => {};
  const handler = (e) => callback(e.detail);
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

/**
 * Firebase Analytics cannot be fully un-initialised once getAnalytics() has run:
 * it has already created a Firebase Installations ID in IndexedDB and set _ga
 * cookies. Withdrawal therefore means: stop collection, remove the artefacts,
 * reload. The reload is the only honest way to guarantee a clean slate.
 */
export async function purgeAnalyticsArtifacts() {
  if (typeof document === 'undefined') return;

  const host = window.location.hostname;
  const parts = host.split('.');
  
  // Google Analytics can be stubborn depending on whether it used a leading dot or not.
  // We'll nuke it across all possible domain variations.
  const possibleDomains = [host, `.${host}`];
  if (parts.length > 2) {
    const parent = parts.slice(-2).join('.');
    possibleDomains.push(parent, `.${parent}`);
  }

  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    
    if (!name.startsWith('_ga') && name !== '_gid' && name !== '_clck' && name !== '_clsk') continue;
    
    // Attempt deletion without domain
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=/`;
    // Attempt deletion with every domain variation
    for (const domain of possibleDomains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=/; domain=${domain}`;
    }
  }

  // Firebase Installations persists a device identifier here.
  if (window.indexedDB?.databases) {
    try {
      const dbs = await window.indexedDB.databases();
      dbs
        .filter((db) => db.name && /firebase|firebaseLocalStorage/i.test(db.name))
        .forEach((db) => window.indexedDB.deleteDatabase(db.name));
    } catch {
      /* Safari and older Firefox do not expose databases(); nothing to do. */
    }
  }
}

// Auto-purge on load if consent is explicitly false.
// This defeats Google Analytics' beforeunload hooks which often try to rewrite 
// the _ga cookies to save engagement time exactly as the page is reloading!
if (typeof window !== 'undefined') {
  const initConsent = getConsent();
  if (initConsent && initConsent.analytics === false) {
    purgeAnalyticsArtifacts();
  }
}
